import { type JSX, lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { Toaster } from 'sonner'
import { routes } from '@/common/constants/routes'
import { BottomNav } from '@/components/layout/bottom-nav'
import { ErrorBoundary } from '@/components/error-boundary'
import { Header } from '@/components/layout/header'
import { Sidebar } from '@/components/layout/sidebar'
import { ProtectedRoute } from '@/components/shared/protected-route'
import { SidebarInset, SidebarProvider } from '@/components/ui/sidebar'
import { useModal } from '@/contexts/modal-context'
import { LoginView } from '@/features/auth/views/login-view'
import { SignupView } from '@/features/auth/views/signup-view'
import { useSearchShortcut } from '@/hooks/use-search-shortcut'

// Lazy loaded views
const CalendarView = lazy(() => {
  return import('@/features/calendar/views/calendar-view').then((m) => {
    return {
      default: m.CalendarView,
    }
  })
})

const CasesView = lazy(() => {
  return import('@/features/cases/views/cases-view').then((m) => {
    return {
      default: m.CasesView,
    }
  })
})

const ClientsView = lazy(() => {
  return import('@/features/clients/views/clients-view').then((m) => {
    return {
      default: m.ClientsView,
    }
  })
})

const TeamView = lazy(() => {
  return import('@/features/team/views/team-view').then((m) => {
    return {
      default: m.TeamView,
    }
  })
})

const SettingsView = lazy(() => {
  return import('@/features/settings/views/settings-view').then((m) => {
    return {
      default: m.SettingsView,
    }
  })
})

const HelpView = lazy(() => {
  return import('@/features/help/views/help-view').then((m) => {
    return {
      default: m.HelpView,
    }
  })
})

const FinancialsView = lazy(() => {
  return import('@/features/financials/views/financials-view').then((m) => {
    return {
      default: m.FinancialsView,
    }
  })
})
const ResearchView = lazy(() => {
  return import('@/features/research/views/research-view').then((m) => {
    return {
      default: m.ResearchView,
    }
  })
})
const ProfileView = lazy(() => {
  return import('@/features/profile/views/profile-view').then((m) => {
    return {
      default: m.ProfileView,
    }
  })
})
const CaseModal = lazy(() => {
  return import('@/features/cases/components/case-modal').then((m) => {
    return {
      default: m.CaseModal,
    }
  })
})
const SearchModal = lazy(() => {
  return import('@/components/shared/search-modal').then((m) => {
    return {
      default: m.SearchModal,
    }
  })
})
const Dashboard = lazy(() => {
  return import('@/features/dashboard/views/dashboard').then((m) => {
    return {
      default: m.Dashboard,
    }
  })
})

const AppContent = (): JSX.Element => {
  const {
    setIsShowCaseModal,
    setIsShowSearchModal,
    isShowCaseModal,
    isShowSearchModal,
  } = useModal()

  // Register global search shortcut (Cmd+K/Ctrl+K)
  useSearchShortcut(() => {
    setIsShowSearchModal(!isShowSearchModal)
  })

  return (
    <>
      <Routes>
        <Route element={<LoginView />} path={routes.LOGIN} />
        <Route element={<SignupView />} path={routes.SIGNUP} />
        <Route
          path="*"
          element={
            <ProtectedRoute>
              <SidebarProvider>
                <Sidebar />
                <SidebarInset
                  className="
                    bg-gray-50
                    dark:bg-gray-950
                  "
                >
                  <div
                    className="
                      relative flex min-h-screen flex-1 flex-col overflow-hidden
                    "
                  >
                    <Header />

                    <main
                      className="
                        flex-1 overflow-y-auto scroll-smooth px-4 py-6 pb-24
                        md:w-full md:pb-8
                        lg:mx-auto lg:max-w-6xl
                      "
                    >
                      <Suspense
                        fallback={
                          <div
                            className="
                              flex min-h-[400px] animate-pulse flex-col
                              items-center justify-center
                            "
                          >
                            <div
                              className="
                                mb-4 size-12 rounded-2xl bg-teal-100
                                dark:bg-teal-900/30
                              "
                            />
                            <div
                              className="
                                h-4 w-32 rounded-lg bg-gray-100
                                dark:bg-gray-800
                              "
                            />
                          </div>
                        }
                      >
                        <Routes>
                          <Route
                            path={routes.DASHBOARD}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <Dashboard />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.CALENDAR}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <CalendarView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.CLIENTS}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <ClientsView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={`${routes.CLIENTS}/:id`}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <ClientsView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.TEAM}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <TeamView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.HELP}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <HelpView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.SETTINGS}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <SettingsView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.BILLING}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <FinancialsView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.RESEARCH}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <ResearchView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.PROFILE}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-bottom-4
                                  "
                                >
                                  <ProfileView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={routes.CASES}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <CasesView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                          <Route
                            path={`${routes.CASES}/:id`}
                            element={
                              <ErrorBoundary>
                                <div
                                  className="
                                    animate-in duration-300 fade-in
                                    slide-in-from-right-4
                                  "
                                >
                                  <CasesView />
                                </div>
                              </ErrorBoundary>
                            }
                          />
                        </Routes>
                      </Suspense>
                    </main>

                    <BottomNav />

                    <Suspense fallback={undefined}>
                      {isShowCaseModal && (
                        <CaseModal
                          onClose={() => {
                            setIsShowCaseModal(false)
                          }}
                        />
                      )}
                      {isShowSearchModal && (
                        <SearchModal
                          onClose={() => {
                            setIsShowSearchModal(false)
                          }}
                        />
                      )}
                    </Suspense>
                  </div>
                </SidebarInset>
              </SidebarProvider>
            </ProtectedRoute>
          }
        />
      </Routes>
      <Toaster />
    </>
  )
}

export { AppContent }
